/**
 * Card contracts — DESIGN.md §6.2–§6.5, §6.11, §6.12, §7.1, §12.2.
 *
 * "The card is the atom" (§1.2). Two read shapes are defined here and the
 * distinction matters for R19: `taskSummary` is what a list row needs and is
 * cheap to fetch a hundred at a time; `taskDetail` adds the description body,
 * relationships, and backlinks that only the card panel reads.
 */
import { z } from 'zod';
import { COLLABORATOR_REASONS } from '../enums.js';
import { richTextSchema } from '../richtext/schema.js';
import { prioritySchema, viewConfigSchema } from './view-config.js';
import { dateSchema, instantSchema, name, paginationSchema, positionSchema, TITLE_MAX_LENGTH, uuidSchema, } from './common.js';
import { userRefSchema } from './users.js';
import { pollSchema } from './polls.js';
import { statusSchema, tagSchema } from './lookups.js';
import { projectRefSchema } from './projects.js';
/** One card ↔ project membership, with its per-project placement (§6.3). */
export const taskProjectSchema = z.object({
    project: projectRefSchema,
    sectionId: uuidSchema.nullable(),
    sectionName: z.string().nullable(),
    /**
     * The section's own fractional position (§6.3.1) — what a section-ordered
     * list sorts by, and therefore what its pagination cursor has to carry.
     */
    sectionPosition: z.string().nullable(),
    position: z.string(),
});
export const collaboratorSchema = z.object({
    user: userRefSchema,
    reason: z.enum(COLLABORATOR_REASONS),
    addedAt: z.string(),
});
/** The minimal card shape used by dependency lists, backlinks, and subtasks. */
export const taskRefSchema = z.object({
    id: uuidSchema,
    number: z.number().int().positive(),
    title: z.string(),
    statusName: z.string(),
    statusCategory: z.string(),
    statusColor: z.string(),
    completedAt: z.string().nullable(),
    assignee: userRefSchema.nullable(),
    dueDate: z.string().nullable(),
});
/**
 * Custom field values, keyed by field id. The value shape follows the field's
 * type (§6.7): string, number, boolean, ISO date, an option id, or an array of
 * option ids / user ids.
 */
export const customFieldValuesSchema = z.record(uuidSchema, z.union([z.string(), z.number(), z.boolean(), z.array(z.string()), z.null()]));
export const taskSummarySchema = z.object({
    id: uuidSchema,
    number: z.number().int().positive(),
    title: z.string(),
    /**
     * The identifier mark — `26090101`, card #54. On the summary and not only the
     * detail, because it is a *list* column and a list is what a summary feeds.
     *
     * Null for cards raised before the mark existed. That is honest rather than
     * tidy: a default would claim a date the card does not have.
     */
    mark: z.string().nullable(),
    status: statusSchema,
    priority: prioritySchema.nullable(),
    assignee: userRefSchema.nullable(),
    startDate: z.string().nullable(),
    dueDate: z.string().nullable(),
    completedAt: z.string().nullable(),
    estimateMinutes: z.number().int().nullable(),
    /** Sum of time entries — §6.13. Zero rather than null so sorting behaves. */
    loggedMinutes: z.number().int().nonnegative(),
    parentId: uuidSchema.nullable(),
    /** §6.5 — at least one incomplete blocker. Advisory, shown as a badge. */
    isBlocked: z.boolean(),
    tags: z.array(tagSchema),
    projects: z.array(taskProjectSchema),
    customFields: customFieldValuesSchema,
    commentCount: z.number().int().nonnegative(),
    attachmentCount: z.number().int().nonnegative(),
    subtaskCount: z.number().int().nonnegative(),
    subtaskDoneCount: z.number().int().nonnegative(),
    createdBy: userRefSchema,
    updatedBy: userRefSchema,
    createdAt: z.string(),
    updatedAt: z.string(),
    /**
     * R42, §7.5 — per-viewer, and on the row rather than only the detail
     * because My Tasks has to *group* by it ("Snoozed cards sit in a collapsed
     * 'Snoozed' section and rejoin their group on the date").
     */
    snoozedUntil: z.string().nullable(),
});
/** One "Referenced by" row — R35, §6.2. */
export const taskReferenceSchema = z.object({
    source: taskRefSchema,
    /** Null when the reference came from the source card's description. */
    commentId: uuidSchema.nullable(),
    snippet: z.string(),
    createdAt: z.string(),
});
export const recurrenceRuleSchema = z
    .object({
    freq: z.enum(['daily', 'weekly', 'monthly']),
    interval: z.number().int().min(1).max(365).default(1),
    byWeekday: z.array(z.number().int().min(0).max(6)).max(7).optional(),
    byMonthDay: z.number().int().min(1).max(31).optional(),
    basis: z.enum(['due_date', 'completion_date']).default('due_date'),
})
    .strict();
/**
 * A card that was merged into this one — card #22, §6.16.
 *
 * Brad's decision: "just link them and close the second but allow the merged
 * card to show the original so the original's context is retained." Nothing
 * moves, so the survivor cannot show the duplicate's comments and time as its
 * own — it shows *whose* they are and how many, and the reader expands to
 * read them on the card that still owns them.
 *
 * The counts ride along because a summary line that cannot say "4 comments,
 * 1h 20m" is not enough to decide whether expanding is worth it.
 */
export const mergedCardSchema = z.object({
    card: taskRefSchema,
    mergedAt: z.string(),
    mergedBy: userRefSchema.nullable(),
    commentCount: z.number().int().nonnegative(),
    loggedMinutes: z.number().int().nonnegative(),
});
export const taskDetailSchema = taskSummarySchema.extend({
    /** ProseMirror JSON (D7) — never HTML. */
    description: richTextSchema.nullable(),
    /**
     * How to verify this card (#8). A field rather than a comment because it is
     * the current answer to a standing question, not a thing that happened —
     * a tester should never have to scroll a thread to find the newest version.
     */
    testingInstructions: richTextSchema.nullable(),
    metadata: z.record(z.unknown()),
    collaborators: z.array(collaboratorSchema),
    /** §6.5 — this card is blocked by these. */
    blockedBy: z.array(taskRefSchema),
    /** …and these are waiting on it. */
    blocking: z.array(taskRefSchema),
    subtasks: z.array(taskRefSchema),
    parent: taskRefSchema.nullable(),
    referencedBy: z.array(taskReferenceSchema),
    /** §6.16 — this card was merged into that one, and is Cancelled (#22). */
    duplicateOf: taskRefSchema.nullable(),
    /** …and these were merged into this one. The survivor's side of #22. */
    mergedFrom: z.array(mergedCardSchema),
    recurrence: recurrenceRuleSchema.nullable(),
    recurredFromId: uuidSchema.nullable(),
    completedBy: userRefSchema.nullable(),
    deletedAt: z.string().nullable(),
    /** §6.13 — logged minutes broken down per person for the Time row. */
    loggedByUser: z.array(z.object({ user: userRefSchema, minutes: z.number().int() })),
    /**
     * #77 — the poll on this card, or null for the great majority of cards that
     * are not polls.
     *
     * Here rather than on its own endpoint because a poll *is* a card: one
     * request already fetches the description, the collaborators and the
     * dependencies, and the panel would otherwise have to make a second one to
     * discover whether there is anything to render.
     *
     * What it contains depends on who is asking — see `Poll`. That is the point:
     * the results-before-voting rule is kept by leaving the numbers out of the
     * payload, not by asking the client not to draw them.
     */
    poll: pollSchema.nullable(),
});
// ---------------------------------------------------------------- requests
export const createTaskRequestSchema = z
    .object({
    title: name(TITLE_MAX_LENGTH),
    description: richTextSchema.nullish(),
    testingInstructions: richTextSchema.nullish(),
    statusId: uuidSchema.optional(),
    priority: prioritySchema.nullish(),
    assigneeId: uuidSchema.nullish(),
    startDate: dateSchema.nullish(),
    dueDate: dateSchema.nullish(),
    estimateMinutes: z
        .number()
        .int()
        .min(0)
        .max(60 * 24 * 365)
        .nullish(),
    /** Adds the card to one project on creation — the common flow (§6.3). */
    projectId: uuidSchema.nullish(),
    sectionId: uuidSchema.nullish(),
    position: positionSchema.optional(),
    parentId: uuidSchema.nullish(),
    tagIds: z.array(uuidSchema).max(50).default([]),
    customFields: customFieldValuesSchema.optional(),
    metadata: z.record(z.unknown()).optional(),
    collaboratorIds: z.array(uuidSchema).max(100).default([]),
    recurrence: recurrenceRuleSchema.nullish(),
    /** §6.15 — pre-fill from a template; every field stays overridable. */
    templateId: uuidSchema.nullish(),
})
    .strict();
/**
 * §6.2 concurrency: the client echoes the `updated_at` it last saw, and a
 * mismatch returns 409 with the current card so the UI can refresh rather
 * than silently clobber someone else's edit.
 */
export const updateTaskRequestSchema = z
    .object({
    title: name(TITLE_MAX_LENGTH).optional(),
    description: richTextSchema.nullish(),
    testingInstructions: richTextSchema.nullish(),
    statusId: uuidSchema.optional(),
    priority: prioritySchema.nullish(),
    assigneeId: uuidSchema.nullish(),
    startDate: dateSchema.nullish(),
    dueDate: dateSchema.nullish(),
    estimateMinutes: z
        .number()
        .int()
        .min(0)
        .max(60 * 24 * 365)
        .nullish(),
    parentId: uuidSchema.nullish(),
    recurrence: recurrenceRuleSchema.nullish(),
    lastSeenUpdatedAt: instantSchema.optional(),
})
    .strict();
/** The list engine — §7.1. One endpoint behind every card surface. */
export const taskQueryRequestSchema = z
    .object({
    config: viewConfigSchema,
    cursor: z.string().max(2048).nullish(),
    limit: z.number().int().min(1).max(200).default(100),
})
    .strict();
export const setTaskTagsRequestSchema = z.object({ tagIds: z.array(uuidSchema).max(50) }).strict();
export const setTaskFieldRequestSchema = z
    .object({ value: z.union([z.string(), z.number(), z.boolean(), z.array(z.string()), z.null()]) })
    .strict();
export const updateMetadataRequestSchema = z
    .object({
    set: z.record(z.unknown()).optional(),
    unset: z.array(z.string().min(1).max(200)).max(100).optional(),
})
    .strict();
/** §12.2 `PUT /tasks/:id/projects` — add, remove, move section, or reorder. */
export const setTaskProjectsRequestSchema = z
    .object({
    memberships: z
        .array(z.object({
        projectId: uuidSchema,
        sectionId: uuidSchema.nullish(),
        position: positionSchema.optional(),
    }))
        .max(20),
})
    .strict();
export const dependencyRequestSchema = z.object({ dependsOnId: uuidSchema }).strict();
export const addCollaboratorRequestSchema = z
    .object({
    userIds: z.array(uuidSchema).max(100).default([]),
    groupIds: z.array(uuidSchema).max(50).default([]),
})
    .strict();
export const completeTaskRequestSchema = z
    .object({
    /** §6.5 — the UI asks before completing a blocked card; this records that. */
    force: z.boolean().default(false),
})
    .strict();
export const listSubtasksQuerySchema = paginationSchema;
/** §6.16 — advisory duplicate suggestions while a title is being typed. */
export const similarTasksQuerySchema = z.object({
    title: z.string().trim().min(3).max(500),
    excludeId: uuidSchema.optional(),
});
export const markDuplicateRequestSchema = z.object({ ofTaskId: uuidSchema }).strict();
export const snoozeRequestSchema = z.object({ until: dateSchema }).strict();
//# sourceMappingURL=tasks.js.map